type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null;
}

function safeJson(value: unknown): string | undefined {
  try {
    return JSON.stringify(value);
  } catch {
    return undefined;
  }
}

function getErrorMessage(value: unknown): string {
  if (value instanceof Error) {
    return value.message || value.name || 'Error';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value);
  }

  if (isRecord(value)) {
    // Common patterns: `{ message }`, `{ error }`, `{ errors }`
    const maybeMessage = value.message;

    if (typeof maybeMessage === 'string' && maybeMessage.trim()) {
      return maybeMessage;
    }

    const json = safeJson(value);

    if (json) {
      return json;
    }
  }

  // Fall back to best-effort coercion
  try {
    return String(value);
  } catch {
    return 'Unknown error';
  }
}

/**
 * Convert `unknown` into a real `Error` so logs and Sentry have a stable shape.
 */
export function toError(value: unknown, fallbackMessage = 'Unknown error'): Error {
  if (value instanceof Error) {
    return value;
  }

  const message = getErrorMessage(value) || fallbackMessage;
  const err = new Error(message);

  if (isRecord(value)) {
    // Preserve additional details without breaking message/stack.
    (err as any).cause = value;
  }

  return err;
}

/**
 * Human-readable, stable formatting for logs/API responses.
 */
export function formatError(value: unknown): string {
  const err = toError(value);
  return err.stack || `${err.name}: ${err.message}`;
}
